import React, { useCallback, useMemo } from 'react';
import DocumentTitle from '../../shared/components/DocumentTitle';
import * as types from '../../store/constants';
import { ACTION_DECREASE, ACTION_INCREASE, ACTION_SET } from '../../store/constants';
import { ProductMetaData, ProductSorting, UsageCondition } from '../../../models/Product';
import { Article, SubscriptionPeriodType } from '../../../models/Article';
import { SelectionList } from '../../../models/SelectionList';
import { getGroupItemId, GroupItem } from '../../../models/GroupItem';
import { useCartGroupedItems } from '../../api/shopping-cart/useCartGroupedItems';
import { useShoppingCartProducts } from '../../api/shopping-cart/useShoppingCartProducts';
import { ContainerInnerWithColumns } from '../../shared/common/ContainerInner';
import { LoadingWrapper } from '../../shared/components/LoadingWrapper';
import LoadingOverlay from '../../shared/components/LoadingOverlay';
import { CartHeader } from './CartHeader';
import FilterBar from '../../shared/common/FilterBar';
import { FilterAndProductListWrapper } from '../../shared/common/FilterAndProductListWrapper';
import { ResponsiveFilterBar } from '../../shared/filter/ResponsiveFilterBar';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../shared/components/NoElementsFound';
import ProductList from '../../shared/common/ProductList';
import CartProductTile from './CartProductTile';
import CartListItem from './CartListItem';
import Pagination from '../../shared/common/Pagination';
import GroupedByList from '../../shared/common/GroupedByList';
import { CartSelectionlistItem } from './CartSelectionlistItem';
import GroupedByBundleItem from '../../shared/bundles/GroupedByBundleItem';
import { useBulkChangeSubscriptionPeriod } from './useBulkChangeSubscriptionPeriod';
import { AuthenticationState } from '../../store/authentication/state';
import { fetchCartAggregationsQuickSearch } from '../../api/shopping-cart/fetchCartAggregationsQuicksearch';
import { defaultVisibleFilterGroups } from '../../shared/filter/defaultVisibleFilterGroups';
import { invalidateCartAggregations, useCartAggregations } from './useCartAggregations';
import { useBulkUpdateUsageConditions } from '../../api/shopping-cart/bulkUpdateUsageConditions';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useCopyBulkToSelectionList } from '../../api/selection-list/useCopyToList';
import { ListSourceType } from '../../../models/ListSourceType';
import { useMoveBulkToSelectionList } from '../../api/selection-list/useMoveToList';
import { useChangePriceInCart } from '../../api/shopping-cart/useChangePriceInCart';
import { useCopyBundleGroupToSelectionList } from '../../api/selection-list/useCopyBundleGroupToList';
import { useMoveBundleGroupToSelectionList } from '../../api/selection-list/useMoveBundleGroupToList';
import { useMoveSingleToSelectionList } from '../../api/selection-list/useMoveSingleToList';
import { useCopySingleToSelectionList } from '../../api/selection-list/useCopySingleToList';
import {
    isSingleCopyOptions,
    isSingleMoveOptions,
    SingleAddOptions,
    SingleCopyOrMoveOptions
} from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../shared/common/CheckBeforeAddingModal';
import { useRemoveBulkFromCart } from '../../api/shopping-cart/useRemoveBulkFromCart';
import { useRemoveProductFromCart } from '../../api/shopping-cart/useRemoveProductFromCart';
import { useDeleteCart } from '../../api/shopping-cart/useDeleteCart';
import { useDownloadCsv } from '../../api/shopping-cart/useDownloadCsv';
import { useAddProductsToGroupFromCart } from '../../api/library-managed/addProductsToGroupFromCart';
import { useRemoveGroupedItemFromCart } from '../../api/shopping-cart/useRemoveGroupedItemFromCart';
import { useChangeAmountGroupedItem } from '../../api/shopping-cart/useChangeAmountGroupedItem';
import { useChangeAmountSingleProductInCart } from '../../api/shopping-cart/useChangeAmountSingleProductInCart';
import { useChangeAmountBulkInCart } from '../../api/shopping-cart/useChangeAmountBulkInCart';
import { MAX_SELECT, useProductSelection } from '../../shared/product-selection/useProductSelection';
import {
    getProductSelectionCount,
    hasProductSelection,
    nothingSelected
} from '../../shared/product-selection/ProductSelectionUtils';
import { ExcludeProductSelection } from '../../shared/product-selection/ProductSelectionTypes';
import { PriceTier } from '../../../models/PriceTier';
import { UnavailableProductsModal } from '../../shared/common/UnavailableProductsModal';
import { useCreateNewSelectionList } from '../../api/selection-list/useCreateNewSelectionList';
import { useDisplayTypeSearchParam } from '../../shared/utils/useDisplayTypeSearchParam';
import { useDisplayFilterSearchParam } from '../../shared/utils/useDisplayFilterSearchParam';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';
import { showNotification, TYPE_WARNING } from '../../shared/notification/toastNotification';
import { usePaginationAndSortParams } from '../../shared/hooks/usePaginationAndSortParams';
import { useProductFilters } from '../../shared/filter/useProductFilters';
import { useGroupBySearchParam } from '../../shared/common/useGroupBySearchParam';

export const DEFAULT_PAGE_SIZE = 54;

export const CartPage = () => {
    const { t } = useTranslation();
    const authentication = useAuthentication();

    const { parsedGrouping } = useGroupBySearchParam();

    const isGroupedBy = parsedGrouping.key !== types.GROUPBY_PRODUCTS;

    const { selectAll, cancelSelection, toggleSelectionMode, onSelectButtonClick, productSelection } =
        useProductSelection();

    const { displayFilter } = useDisplayFilterSearchParam();

    const { page, sortBy, pageSize } = usePaginationAndSortParams({
        defaultSortBy: ProductSorting.TITLE_AZ,
        defaultPageSize: DEFAULT_PAGE_SIZE
    });

    const { productFilter } = useProductFilters();

    const { display } = useDisplayTypeSearchParam();

    const { products, isLoading: isProductsLoading } = useShoppingCartProducts({
        pagination: {
            page,
            pageSize,
            sortBy
        },
        filterMap: productFilter,
        groupType: parsedGrouping.key,
        groupId: parsedGrouping.id
    });

    const { data: groupedItems, isInitialLoading: isGroupedItemsLoading } = useCartGroupedItems({
        groupKey: parsedGrouping.key ?? types.GROUPBY_PRODUCTS,
        enabled: isGroupedBy
    });

    const bulkChangeSubscriptionPeriodMutation = useBulkChangeSubscriptionPeriod(productSelection, productFilter);

    const { isLoading: isLoadingChangeAmountSingle, changeAmount } = useChangeAmountSingleProductInCart();

    const { changeAmountBulk } = useChangeAmountBulkInCart(productSelection, productFilter);

    const { mutate: createSelectionList } = useCreateNewSelectionList();

    const { isLoading: isLoadingRemoveSingle, mutateAsync: removeProductFromCart } = useRemoveProductFromCart();

    const { isLoading: isLoadingRemoveBulk, removeBulkFromCart } = useRemoveBulkFromCart({
        onSuccess: () => {
            cancelSelection();
        },
        productSelection,
        productFilter
    });

    const { copyToList } = useCopySingleToSelectionList({ source: ListSourceType.CART });
    const { moveToList } = useMoveSingleToSelectionList({ source: ListSourceType.CART });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions | SingleCopyOrMoveOptions) => {
            if (isSingleCopyOptions(options) && options.target === ListActionTarget.List) {
                return copyToList(options.options, options.listId, performChecks);
            } else if (isSingleMoveOptions(options) && options.target === ListActionTarget.List) {
                return moveToList(options.options, options.listId, performChecks);
            } else {
                throw new Error('illegal cart action');
            }
        },
        [copyToList, moveToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } = useListContentChecks<
        SingleAddOptions | SingleCopyOrMoveOptions
    >({ onAction });

    const bulkUpdateUsageConditions = useBulkUpdateUsageConditions();

    async function onUpdateUsageCondition(
        product: ProductMetaData & Article,
        usageCondition: UsageCondition,
        subscriptionPeriodType?: SubscriptionPeriodType
    ) {
        const sourceUsageCondition = product.price
            ? {
                  lendingModel: product.price.lendingModel!,
                  usageLimit: product.price.usageLimit!,
                  termLimit: product.price.termLimit!
              }
            : {};
        const sourceSubscriptionPeriodType = product.subscriptionPeriod?.type;
        const targetSubscriptionPeriodType = subscriptionPeriodType ?? product.subscriptionPeriod?.type;

        await bulkUpdateUsageConditions([
            {
                productId: product.id,
                sourceUsageCondition: sourceUsageCondition,
                targetUsageCondition: usageCondition,
                sourceSubscriptionPeriodType,
                targetSubscriptionPeriodType
            }
        ]);
    }

    const { isLoading: isLoadingRemoveGroupedItemFromCart, removeGroupedItemFromCart } = useRemoveGroupedItemFromCart();

    async function onRemoveGroupedItemFromCart(item: GroupItem) {
        const group = { ...parsedGrouping, id: getGroupItemId(item) };
        await removeGroupedItemFromCart(group);
    }

    const { copyToListBulk: copyBundleGroupToList } = useCopyBundleGroupToSelectionList({
        source: ListSourceType.CART
    });

    function onCopyBundleGroupToList(item: GroupItem, list: SelectionList, performChecks: boolean) {
        return copyBundleGroupToList(getGroupItemId(item), list.id, performChecks);
    }

    const { isLoading: isLoadingMoveGroupToList, moveToListBulk: moveBundleGroupToList } =
        useMoveBundleGroupToSelectionList({ source: ListSourceType.CART });

    function onMoveBundleGroupToList(item: GroupItem, list: SelectionList, performChecks: boolean) {
        return moveBundleGroupToList(getGroupItemId(item), list.id, performChecks);
    }

    const { changeAmountGroupedItem } = useChangeAmountGroupedItem();

    function onGroupAmountChange(
        item: GroupItem,
        amount: number,
        command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE
    ) {
        const group = {
            ...parsedGrouping,
            id: getGroupItemId(item)
        };
        return changeAmountGroupedItem(group, amount, command);
    }

    const { addToGroup } = useAddProductsToGroupFromCart(productFilter, productSelection);

    const downloadCsv = useDownloadCsv();

    const { changePriceBulk } = useChangePriceInCart(productFilter);

    const { mutate: deleteCart, isLoading: isDeletingCard } = useDeleteCart();

    const { copyToListBulk: copyBulkProductsToList } = useCopyBulkToSelectionList({
        filter: productFilter,
        source: ListSourceType.CART,
        productSelection
    });

    const onBulkCopyToList = (list: SelectionList, performChecks: boolean) => {
        return copyBulkProductsToList(list.id, performChecks);
    };

    const { isLoading: isLoadingMoveToList, moveToListBulk: moveBulkProductsToList } = useMoveBulkToSelectionList({
        source: ListSourceType.CART,
        onSuccess: () => {
            cancelSelection();
        },
        productSelection,
        productFilter
    });

    const onBulkMoveToList = (list: SelectionList, performChecks: boolean) => {
        return moveBulkProductsToList(list.id, performChecks);
    };

    const onChangePriceAll = (priceTier: PriceTier, performChecks: boolean) => {
        const productSelection: ExcludeProductSelection = {
            excluded: [],
            totalCount: products.count
        };

        if (productSelection.totalCount > MAX_SELECT) {
            showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING);
            throw new Error('max selection exceeded');
        }

        return changePriceBulk(priceTier, performChecks, productSelection);
    };

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(() => cartPageFilterGroups(authentication), [authentication]);
    const { aggregationsQueryKeyBuilder, fetchAggregations } = useCartAggregations(parsedGrouping);
    const fetchCartAggregationsQuickSearchCallback = useCallback(fetchCartAggregationsQuickSearch, []);
    //endregion

    const isNotGroupExpanded = isGroupedBy && !parsedGrouping.id;

    const itemComponent = parsedGrouping.key === types.GROUPBY_LISTS ? CartSelectionlistItem : GroupedByBundleItem;

    const loading =
        isProductsLoading ||
        isGroupedItemsLoading ||
        isDeletingCard ||
        isLoadingMoveToList ||
        isLoadingMoveGroupToList ||
        isLoadingRemoveBulk ||
        isLoadingRemoveGroupedItemFromCart ||
        isLoadingChangeAmountSingle ||
        isLoadingRemoveSingle;

    return (
        <DocumentTitle title={t('components.documentTitle.cart')}>
            <ContainerInnerWithColumns>
                {loading ? (
                    <LoadingWrapper>
                        <LoadingOverlay loading={loading} />
                    </LoadingWrapper>
                ) : null}
                <>
                    <CartHeader
                        isInSelectionMode={hasProductSelection(productSelection)}
                        cancelSelectionMode={cancelSelection}
                        onMoveCartToList={onBulkMoveToList}
                        onMoveToList={onBulkMoveToList}
                        onCopyCartToList={onBulkCopyToList}
                        onCopyToList={onBulkCopyToList}
                        onDelete={deleteCart}
                        onCreateNewListClicked={createSelectionList}
                        onChangePrice={(priceTier, performChecks) =>
                            changePriceBulk(priceTier, performChecks, productSelection)
                        }
                        onDownloadCSV={downloadCsv}
                        onCopyAmountChange={changeAmountBulk}
                        onRemoveFromCart={removeBulkFromCart}
                        bulkChangeSubscriptionPeriodMutation={bulkChangeSubscriptionPeriodMutation}
                        totalProducts={products.count}
                        onBulkAddToGroup={addToGroup}
                        productSelectionCount={getProductSelectionCount(productSelection)}
                        isNothingSelected={nothingSelected(productSelection)}
                    />
                    <FilterBar
                        sortBy={sortBy}
                        productSelection={productSelection}
                        displayType={display}
                        totalProducts={products.count}
                        loading={loading}
                        onChangePrice={onChangePriceAll}
                        pageSize={pageSize}
                        grouping={parsedGrouping}
                        toggleSelectionMode={toggleSelectionMode}
                        selectAll={() => selectAll(products.count)}
                        groupByGroups={[
                            {
                                key: types.GROUPBY_LISTS,
                                label: 'Selection Lists'
                            }
                        ]}
                        hidePagination={isGroupedBy}
                        hideSelectButtons={isNotGroupExpanded}
                        hideSelectAll={isGroupedBy}
                    />
                </>
                <FilterAndProductListWrapper>
                    {displayFilter ? (
                        <ResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchCartAggregationsQuickSearchCallback}
                        />
                    ) : null}
                    <div className={classnames('ca-main', { 'min-h-screen': loading })}>
                        {parsedGrouping.key === types.GROUPBY_PRODUCTS ? (
                            !loading && products.count === 0 ? (
                                <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                            ) : (
                                <div className="ca-main-inner">
                                    <ProductList
                                        products={products.list}
                                        displayType={display}
                                        grouping={parsedGrouping}
                                        productSelection={productSelection}
                                        onSelectProduct={onSelectButtonClick}
                                        onAddPrice={changePrice}
                                        isProductSelectable={true}
                                    >
                                        {({ key, isListView, ...value }) =>
                                            isListView ? (
                                                <CartListItem
                                                    {...value}
                                                    key={key}
                                                    onRemoveProductFromList={() => removeProductFromCart(value.product)}
                                                    onCopyAmountChange={(amount, command) =>
                                                        changeAmount(value.product, amount, command)
                                                    }
                                                    onUpdateUsageCondition={(
                                                        newUsageCondition,
                                                        newSubscriptionPeriodType
                                                    ) =>
                                                        onUpdateUsageCondition(
                                                            value.product,
                                                            newUsageCondition,
                                                            newSubscriptionPeriodType
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <CartProductTile
                                                    {...value}
                                                    key={key}
                                                    onRemoveProductFromList={() => removeProductFromCart(value.product)}
                                                    onCopyAmountChange={(amount, command) =>
                                                        changeAmount(value.product, amount, command)
                                                    }
                                                    onUpdateUsageCondition={(
                                                        newUsageCondition,
                                                        newSubscriptionPeriodType
                                                    ) =>
                                                        onUpdateUsageCondition(
                                                            value.product,
                                                            newUsageCondition,
                                                            newSubscriptionPeriodType
                                                        )
                                                    }
                                                />
                                            )
                                        }
                                    </ProductList>
                                    <Pagination totalItems={products.count} pageSize={pageSize} />
                                </div>
                            )
                        ) : groupedItems && groupedItems?.length > 0 ? (
                            <div className="ca-main-inner groupby-cart" key={`${parsedGrouping.key}_group`}>
                                <GroupedByList
                                    totalProducts={products.count}
                                    currentPage={page}
                                    pageSize={pageSize}
                                    products={products.list}
                                    displayType={display}
                                    productSelection={productSelection}
                                    onCopyGroupToList={onCopyBundleGroupToList}
                                    onMoveGroupToList={onMoveBundleGroupToList}
                                    grouping={parsedGrouping}
                                    onGroupAmountChange={onGroupAmountChange}
                                    // FIXME the state typings should allow this without an explicit cast
                                    groups={groupedItems as unknown as ReadonlyArray<GroupItem>}
                                    onSelectProduct={onSelectButtonClick}
                                    onRemoveGroupedItemFromList={onRemoveGroupedItemFromCart}
                                    itemComponent={itemComponent}
                                    cancelProductSelection={cancelSelection}
                                    onAddPrice={changePrice}
                                    invalidateAggregations={invalidateCartAggregations}
                                    isProductSelectable={true}
                                >
                                    {({ key, isListView, ...value }) =>
                                        isListView ? (
                                            <CartListItem
                                                {...value}
                                                key={key}
                                                onRemoveProductFromList={() => removeProductFromCart(value.product)}
                                                onCopyAmountChange={(amount, command) =>
                                                    changeAmount(value.product, amount, command)
                                                }
                                                onUpdateUsageCondition={(
                                                    newUsageCondition,
                                                    newSubscriptionPeriodType
                                                ) =>
                                                    onUpdateUsageCondition(
                                                        value.product,
                                                        newUsageCondition,
                                                        newSubscriptionPeriodType
                                                    )
                                                }
                                            />
                                        ) : (
                                            <CartProductTile
                                                {...value}
                                                key={key}
                                                onRemoveProductFromList={() => removeProductFromCart(value.product)}
                                                onCopyAmountChange={(amount, command) =>
                                                    changeAmount(value.product, amount, command)
                                                }
                                                onUpdateUsageCondition={(
                                                    newUsageCondition,
                                                    newSubscriptionPeriodType
                                                ) =>
                                                    onUpdateUsageCondition(
                                                        value.product,
                                                        newUsageCondition,
                                                        newSubscriptionPeriodType
                                                    )
                                                }
                                            />
                                        )
                                    }
                                </GroupedByList>
                            </div>
                        ) : null}
                    </div>
                </FilterAndProductListWrapper>
            </ContainerInnerWithColumns>
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </DocumentTitle>
    );
};

const cartPageFilterGroups = (authentication: AuthenticationState) => [
    ...defaultVisibleFilterGroups(authentication),
    'selectedLendingModel',
    'selectedUsageCondition',
    'selectedPrice'
];
